import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Select,
  InputLabel,
  FormControl,
  InputAdornment,
  DialogContentText,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useQuery, useMutation } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import { GET_ALL_USERS, GET_ALL_ROLES, ADD_USER, UPDATE_USER, DELETE_USER } from "../queries";
import { useAuth } from "../../../hooks/useAuth";
import { enqueueSnackbar } from "notistack";

const Users: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState({ username: "", password: "", roleId: "", isActive: true });
  const [showPassword, setShowPassword] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);

  const { data: usersData, refetch: refetchUsers } = useQuery(GET_ALL_USERS);
  const { data: rolesData } = useQuery(GET_ALL_ROLES);
  const auth = useAuth();

  const [createUser] = useMutation(ADD_USER, {
    onCompleted: () => {
      setDialogOpen(false);
    },
    onError: (err) => enqueueSnackbar(`Create failed: ${err.message}`, { variant: "error" }),
  });

  const [updateUser] = useMutation(UPDATE_USER, {
    onCompleted: () => {
      setDialogOpen(false);
    },
    onError: (err) => enqueueSnackbar(`Update failed: ${err.message}`, { variant: "error" }),
  });

  const [deleteUser] = useMutation(DELETE_USER, {
    onCompleted: () => {
      enqueueSnackbar("User deleted successfully", { variant: "success" });
      refetchUsers();
      setUserToDelete(null);
    },
    onError: (err) => enqueueSnackbar(`Delete failed: ${err.message}`, { variant: "error" }),
  });

  const openAddDialog = () => {
    setFormState({ username: "", password: "", roleId: "", isActive: true });
    setSelectedUser(null);
    setDialogOpen(true);
  };

  const openEditDialog = (user: any) => {
    const roleId =
      rolesData?.getAllRoles?.find(
        (role: any) => role.role_name === user?.assigned_role
      )?.role_id || "";

    setFormState({ username: user.username, password: "", roleId, isActive: user.is_active });
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleDeleteUser = (userId: string) => {
    setUserToDelete(userId);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    await deleteUser({ variables: { userId: userToDelete } });
    setDeleteDialogOpen(false);
  };

  const handleSubmit = async () => {
    try {
      if (selectedUser) {
        if (formState.roleId === selectedUser.assigned_role) {
          enqueueSnackbar("No changes detected", { variant: "info" });
          return;
        }
        const { data } = await updateUser({
          variables: {
            userId: selectedUser.user_id,
            assignedRole: formState.roleId,
            createdBy: auth.user.userId,
          },
        });

        if (!data?.updateUserRole?.user_id) {
          throw new Error("Failed to update user role");
        }

        enqueueSnackbar("User role updated successfully", { variant: "success" });
        refetchUsers();
        setDialogOpen(false);
        setSelectedUser(null);
      } else {
        const { data } = await createUser({
          variables: {
            username: formState.username,
            password: formState.password,
            assignedRole: formState.roleId,
          },
        });

        if (!data?.createUser?.status) {
          throw new Error(data?.createUser?.message || "Failed to create user");
        }

        enqueueSnackbar("User created successfully", { variant: "success" });
        refetchUsers();
        setDialogOpen(false);
      }
    } catch (err: any) {
      enqueueSnackbar(err.message || "Something went wrong", { variant: "error" });
    }
  };


  const filteredUsers = (usersData?.getAllUsers || []).filter((user: any) =>
    user.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const defaultValue = rolesData?.getAllRoles?.find(
    (role: any) => role.role_name === selectedUser?.assigned_role
  )?.role_id || "";

  useEffect(() => {
    if (selectedUser) {
      const roleId =
        rolesData?.getAllRoles?.find(
          (role: any) => role.role_name === selectedUser?.assigned_role
        )?.role_id || "";

      setFormState((prev) => ({
        ...prev,
        roleId,
      }));
    }
  }, [selectedUser, rolesData]);


  return (
    <Paper elevation={3} sx={{ borderRadius: 2, p: 2 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="bold">Users</Typography>
        {auth.canCreate("USER") && (
          <Button variant="contained" startIcon={<AddIcon />} sx={{ backgroundColor: "#E60012" }} onClick={openAddDialog}>
            Add User
          </Button>
        )}
      </Box>

      {/* Search */}
      <Box mb={2}>
        <TextField
          placeholder="Search by username..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#E60012" }}>
              <TableCell sx={{ color: "#f5f5f5", fontWeight: "bold" }}>ID</TableCell>
              <TableCell sx={{ color: "#f5f5f5", fontWeight: "bold" }}>Username</TableCell>
              <TableCell sx={{ color: "#f5f5f5", fontWeight: "bold" }}>Role</TableCell>
              <TableCell sx={{ color: "#f5f5f5", fontWeight: "bold" }}>Is Active</TableCell>
              {(auth.canCreate("USER") || auth.canUpdate("USER") || auth.canDelete("USER")) && (
                <TableCell sx={{ color: "#f5f5f5", fontWeight: "bold" }}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user: any, idx: number) => (
              <TableRow key={user.user_id} sx={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f5f5f5" }}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.assigned_role}</TableCell>
                <TableCell>{user.is_active ? "true" : "false"}</TableCell>
                {(auth.canCreate("USER") || auth.canUpdate("USER") || auth.canDelete("USER")) && (
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {auth.canUpdate("USER") && (
                        <IconButton color="primary" size="small" onClick={() => openEditDialog(user)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {auth.canDelete("USER") && (
                        <IconButton color="error" size="small" onClick={() => handleDeleteUser(user.user_id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedUser ? "Edit User" : "Add User"}
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Username"
            value={formState.username}
            onChange={(e) => {
              const value = e.target.value;
              if (/^[a-zA-Z0-9]*$/.test(value) && value.length <= 20) {
                setFormState({ ...formState, username: value });
              }
            }}
            fullWidth
            inputProps={{ maxLength: 20, readOnly: !!selectedUser }}
            helperText="Only alphanumeric characters, max 20"
          />

          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            value={formState.password}
            onChange={(e) => {
              // Block spaces and enforce max length 20
              const value = e.target.value.replace(/\s+/g, "");
              if (value.length <= 20) {
                setFormState({ ...formState, password: value });
              }
            }}
            fullWidth
            inputProps={{ minLength: 8, maxLength: 20 }}
            helperText="Leave blank to keep existing password"
            error={formState.password.length > 0 && formState.password.length < 8}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={formState.roleId}
              onChange={(e) => setFormState({ ...formState, roleId: e.target.value })}
              label="Role"
            >
              {rolesData?.getAllRoles?.map((role: any) => (
                <MenuItem key={role.role_id} value={role.role_id}>
                  {role.role_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={formState.isActive ?? true}
                onChange={(e) => setFormState({ ...formState, isActive: e.target.checked })}
              />
            }
            label="Is Active"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !formState.username.trim() ||
              (!selectedUser && !formState.password.trim()) ||
              !formState.roleId ||
              (formState.password.length > 0 && formState.password.length < 8)
            }>
            {selectedUser ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" onClick={confirmDeleteUser}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Users;
